import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import { NodeProps, NodeResizer } from '@xyflow/react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Tooltip, ColorPicker, InputNumber, theme } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { NodeData, useFlowActions, useActivePipelineId } from '../../store/store';
import useTabManagerStore from '../../store/tabManagerStore';

// Type definitions
interface NoteNodeData extends NodeData {
  content?: string;
  width?: number;
  height?: number;
  red?: number;
  green?: number;
  blue?: number;
  fontSize?: number;
}

interface NoteNodeProps extends NodeProps {
  data: NoteNodeData;
}

// Styled components
const NoteContainer = styled.div<{ 
  width: number; 
  height: number; 
  selected?: boolean; 
  backgroundColor: string;
}>`
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  background-color: ${props => props.backgroundColor};
  border: 2px solid ${props => props.selected ? '#1890ff' : '#f0f0f0'};
  border-radius: 8px;
  box-shadow: ${props => props.selected ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  display: flex;
  flex-direction: column;
  position: relative;
  transition: border-color 0.2s ease-in-out;
  min-width: 100px;
  min-height: 60px;
`;

const NoteHeader = styled.div<{ $bgColor?: string; $borderColor?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.$bgColor || '#fafafa'};
  border-bottom: 1px solid ${props => props.$borderColor || '#f0f0f0'};
  min-height: 32px;
  border-radius: 6px 6px 0 0;
  overflow: hidden;
`;

const NoteTitle = styled.span<{ $color?: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color || '#666'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoteContent = styled.div<{ isEditing: boolean }>`
  flex: 1;
  padding: ${props => props.isEditing ? '0' : '8px'};
  overflow: ${props => props.isEditing ? 'hidden' : 'auto'};
  font-size: 12px;
  line-height: 1.4;
  border-radius: 0 0 6px 6px;
`;

const EditArea = styled.textarea<{ fontSize: number }>`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  resize: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: ${props => props.fontSize}px;
  line-height: 1.4;
  padding: 8px;
  background: transparent;
  box-sizing: border-box;
`;

const MarkdownContent = styled.div<{ fontSize: number; $codeBg?: string; $borderColor?: string; $textSecondary?: string }>`
  height: 100%;
  overflow: auto;
  padding: 8px;
  font-size: ${props => props.fontSize}px;
  
  h1, h2, h3, h4, h5, h6 {
    margin: 0.5em 0 0.3em 0;
    font-weight: 600;
    line-height: 1.25;
  }
  
  h1 {
    font-size: 1.5em;
    border-bottom: 1px solid ${props => props.$borderColor || '#eaecef'};
    padding-bottom: 0.2em;
  }
  
  h2 {
    font-size: 1.3em;
  }
  
  h3 {
    font-size: 1.15em;
  }
  
  h4, h5, h6 {
    font-size: 1em;
  }
  
  p {
    margin: 0.3em 0;
  }
  
  ul, ol {
    margin: 0.3em 0;
    padding-left: 1.2em;
  }
  
  code {
    background: ${props => props.$codeBg || '#f5f5f5'};
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.9em;
  }
  
  blockquote {
    margin: 0.3em 0;
    padding-left: 0.8em;
    border-left: 3px solid ${props => props.$borderColor || '#ddd'};
    color: ${props => props.$textSecondary || '#666'};
  }
`;

const NoteNode: React.FC<NoteNodeProps> = ({ id, data, selected }) => {
  const { token } = theme.useToken();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(data.content || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Store actions
  const { updateNode } = useFlowActions();
  const activePipelineId = useActivePipelineId();
  const { updateTabModified } = useTabManagerStore((state) => ({
    updateTabModified: state.updateTabModified,
  }));
  
  // Default dimensions, colors, and font size
  const width = data.width || 200;
  const height = data.height || 120;
  const red = data.red || 254;
  const green = data.green || 254;
  const blue = data.blue || 254;
  const fontSize = data.fontSize || 12;
  
  // Calculate background color from RGB values
  const backgroundColor = `rgb(${red}, ${green}, ${blue})`;

  useEffect(() => {
    setContent(data.content || '');
  }, [data.content]);

  const handleEditToggle = useCallback(() => {
    setIsEditing(!isEditing);
    // Focus textarea when entering edit mode
    if (!isEditing) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [isEditing]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    
    // Update the node data in the store
    if (activePipelineId) {
      updateNode(activePipelineId, id, { 
        data: { 
          ...data, 
          content: newContent 
        } 
      });
      
      // Mark tab as modified
      updateTabModified(activePipelineId, true);
    }
  }, [activePipelineId, id, data, updateNode, updateTabModified]);

  const handleColorChange = useCallback((color: Color) => {
    const { r, g, b } = color.toRgb();
    
    // Update the node data in the store with new color values
    if (activePipelineId) {
      updateNode(activePipelineId, id, { 
        data: { 
          ...data, 
          red: r,
          green: g,
          blue: b
        } 
      });
      
      // Mark tab as modified
      updateTabModified(activePipelineId, true);
    }
  }, [activePipelineId, id, data, updateNode, updateTabModified]);

  const handleFontSizeChange = useCallback((newFontSize: number) => {
    // Update the node data in the store with new font size
    if (activePipelineId) {
      updateNode(activePipelineId, id, { 
        data: { 
          ...data, 
          fontSize: newFontSize
        } 
      });
      
      // Mark tab as modified
      updateTabModified(activePipelineId, true);
    }
  }, [activePipelineId, id, data, updateNode, updateTabModified]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Allow Ctrl+Enter to exit edit mode
    if (e.ctrlKey && e.key === 'Enter') {
      setIsEditing(false);
    }
    // Prevent event bubbling to avoid interfering with ReactFlow
    e.stopPropagation();
  }, []);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Prevent backspace and delete from bubbling up to ReactFlow's global handler
    // This prevents accidental node deletion when editing input fields
    e.stopPropagation();
  }, []);



  const handleResize = useCallback((event: any, data: any) => {
    // Update the node dimensions in the store
    if (activePipelineId) {
      updateNode(activePipelineId, id, { 
        data: { 
          ...data, 
          width: data.width,
          height: data.height
        } 
      });
      
      // Mark tab as modified
      updateTabModified(activePipelineId, true);
    }
  }, [activePipelineId, id, data, updateNode, updateTabModified]);

  return (
    <NoteContainer 
      width={width} 
      height={height} 
      selected={selected}
      backgroundColor={backgroundColor}
      data-testid="note-container"
    >
      <NodeResizer
        color="#1890ff"
        isVisible={selected}
        minWidth={100}
        minHeight={60}
        maxWidth={600}
        maxHeight={400}
        onResize={handleResize}
        handleStyle={{
          width: '12px',
          height: '12px',
          border: '2px solid #1890ff',
          borderRadius: '3px',
          backgroundColor: token.colorBgContainer,
        }}
        lineStyle={{
          borderColor: '#1890ff',
          borderWidth: '2px',
        }}
      />
        <NoteHeader 
          data-testid="note-header"
          $bgColor={token.colorBgLayout}
          $borderColor={token.colorBorder}
        >
          <NoteTitle data-testid="note-title">📝 {data.name || 'Note'}</NoteTitle>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {isEditing && (
              <>
                <Tooltip title="Font size">
                  <InputNumber
                    value={fontSize}
                    onChange={(value) => handleFontSizeChange(value || 12)}
                    onKeyDown={handleInputKeyDown}
                    min={8}
                    max={24}
                    size="small"
                    style={{ width: '50px', height: '24px' }}
                    data-testid="note-font-size-input"
                  />
                </Tooltip>
                <Tooltip title="Change background color">
                  <div onKeyDown={handleInputKeyDown} data-testid="note-color-picker-wrapper">
                    <ColorPicker
                      value={backgroundColor}
                      onChange={handleColorChange}
                      size="small"
                      showText={false}
                      format="rgb"
                      disabledAlpha={true}
                      disabledFormat={true}
                      style={{ width: '24px', height: '24px' }}
                      data-testid="note-color-picker"
                    />
                  </div>
                </Tooltip>
              </>
            )}
            <Tooltip title={isEditing ? 'Preview' : 'Edit'}>
              <Button
                type="text"
                size="small"
                icon={isEditing ? <EyeOutlined /> : <EditOutlined />}
                onClick={handleEditToggle}
                style={{ padding: '2px 4px', height: '24px', width: '24px' }}
                data-testid="note-edit-toggle-button"
              />
            </Tooltip>
          </div>
        </NoteHeader>
        <NoteContent isEditing={isEditing} data-testid="note-content">
          {isEditing ? (
            <EditArea
              ref={textareaRef}
              className="nowheel nodrag"
              fontSize={fontSize}
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter your note content here... (Markdown supported)"
              data-testid="note-edit-textarea"
            />
          ) : (
            <MarkdownContent 
              className="nowheel" 
              fontSize={fontSize} 
              data-testid="note-markdown-content"
              $codeBg={token.colorFillAlter}
              $borderColor={token.colorBorder}
              $textSecondary={token.colorTextSecondary}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content || 'Click edit to add content...'}
              </ReactMarkdown>
            </MarkdownContent>
          )}
        </NoteContent>
    </NoteContainer>
  );
};

export default memo(NoteNode); 